/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CreatePage
 * Reason: Dialog "Neuen Provisionslauf anlegen" on the run list - creates one cp.ProvisionRun through
 *         the metadata-driven form (ProvisionRunForm derives period, valuta and name from the chosen
 *         provision type) and hands the created run to the host, which opens it.
 */
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {MvsCrudObjectComponent} from 'features/core/shared/crud/components/mvs-crud-object/mvs-crud-object.component';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from 'features/core/shared/logic/object-change-information';
import {DtoDetail} from 'features/core/shared/dto/dto.detail';
import {ProvisionRunDto} from '../../../model/dto/entity/provision-run.dto';

/**
 * Dialog "Neuen Provisionslauf anlegen" — the visible way to create a run from the run list.
 *
 * ── THE FORM IS THE BACKEND'S
 *
 * The capture is `<mvs-crud-object [objectType]="'cp.ProvisionRun'">` — no hand-written form. The
 * derivation lives in `ProvisionRunForm` (backend, R-06): `provisionType` is a refresh-on-change
 * field, and once a provision type is chosen the regenerated form carries
 *   * period start = period end of the last NON-aborted run of that type + 1 day,
 *   * period end   = end of that month (runs are strictly monthly, D-19),
 *   * valuta       = period start,
 *   * name         = "{Provisionsart} MM/YYYY",
 * with name, period and status read-only. For the FIRST run of a provision type there is nothing
 * to continue from: the form leaves period and valuta writable and the user enters them. The
 * dialog says both things up front, because before the type is picked the form hides those fields
 * entirely (the screenshot finding "der Dialog sagt nicht, welcher Monat angelegt wird").
 *
 * The summary line under the form mirrors the values the form reports through `onFormChange`, so
 * the period that will be created is stated in plain words next to the button that creates it.
 *
 * ── CALLER-OWNED SAVE (fe_core_crud-object-component Rule 6)
 *
 * The dialog footer owns the button: `hideDefaultActions` + `save()`, gated on the form's own
 * verdict (`onFormSubmitStateChange`), errors through `onObjectChangeError`.
 */
@Component({
    selector: 'cp-run-create-dialog',
    templateUrl: './cp-run-create-dialog.component.html',
    styleUrls: ['./cp-run-create-dialog.component.scss'],
    standalone: false
})
export class CpRunCreateDialogComponent implements OnInit, OnChanges {

    /** Entity alias of the created object. */
    static readonly OBJECT_TYPE: string = 'cp.ProvisionRun';

    readonly objectType: string = CpRunCreateDialogComponent.OBJECT_TYPE;

    busy: boolean;
    initialized: boolean;

    @Input() visible: boolean = false;

    @Output() visibleChange = new EventEmitter<boolean>();

    /** The run was created — the host refreshes its list and opens the run. */
    @Output() provisionRunCreated = new EventEmitter<ProvisionRunDto>();

    /** The create form inside the dialog body; the footer button triggers its own write. */
    @ViewChild('runForm') runForm: MvsCrudObjectComponent;

    /** Rendered only while the dialog is open, so every opening starts with a fresh create form. */
    formActive: boolean = false;

    /** The form's own verdict whether it may be saved now. */
    submittable: boolean = false;

    /** Last values the form reported — drives the "wird angelegt" summary. */
    formValues: ProvisionRunDto | null = null;

    failureMessage: string | null = null;

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize component.
     */
    initComponent(): void {
        this.initialized = true;
    }

    /**
     * Refresh component — reset the dialog to a fresh create form.
     */
    refreshComponent(): void {
        this.busy = false;
        this.submittable = false;
        this.formValues = null;
        this.failureMessage = null;
        this.formActive = true;
    }

    handleShow(): void {
        this.refreshComponent();
    }

    handleVisibleChange(visible: boolean): void {
        this.visible = visible;
        if (!visible) {
            this.formActive = false;
        }
        this.visibleChange.emit(visible);
    }

    handleCancel(): void {
        this.handleVisibleChange(false);
    }

    handleFormSubmitStateChange(submittable: boolean): void {
        this.submittable = submittable;
    }

    handleFormChange(dto: DtoDetail): void {
        this.formValues = dto as ProvisionRunDto;
    }

    /**
     * Trigger the form's own write. `save('create')` returns false when the form refused; the
     * outcome of an accepted write arrives on {@link handleChanged} / {@link handleError}.
     */
    handleCreate(): void {

        if (!this.submittable || this.busy || !this.runForm) {
            return;
        }

        this.failureMessage = null;
        this.busy = this.runForm.save('create');
    }

    /**
     * Handle the result of the CRUD form.
     */
    handleChanged(event: ObjectChangeInformation): void {

        this.busy = false;

        switch (event.action) {
            case ObjectChangeInformationActionEnum.created:
                this.handleVisibleChange(false);
                this.provisionRunCreated.emit(event.after as ProvisionRunDto);
                break;
            case ObjectChangeInformationActionEnum.updated:
            case ObjectChangeInformationActionEnum.deleted:
                // not reachable from a create form; nothing to do
                break;
        }
    }

    handleError(error: any): void {
        this.busy = false;
        this.failureMessage = 'Der Provisionslauf konnte nicht angelegt werden. Bitte prüfen Sie die '
            + 'Angaben und die Meldung des Servers.';
        console.error('Error creating provision run', error);
    }

    /** True once the form carries a derived (or entered) period — the summary line is shown. */
    hasPeriod(): boolean {
        return !!this.formValues?.periodStartDate && !!this.formValues?.periodEndDate;
    }

    /**
     * Process changes within binding.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }
}
